import { GetterTree } from "vuex";
import { MetadataState, MetadataLookup, Metadata } from "./types";
import { RootState } from "../types";

export const getters: GetterTree<MetadataState, RootState> = {
	/**
	 * Finds a metadata type by id or title.
	 */
	getMetadataType: state => {
		return (type: string | number) => {
			if (typeof type == "string") {
				return state.metadataTypes.find(mt => mt.title.toLowerCase() == type.toLowerCase());
			} else if (typeof type == "number") {
				return state.metadataTypes.find(mt => mt.metadataTypeId == type);
			}
		};
	},

	/**
	 * Finds a metadata object type by id or title (table name).
	 */
	getObjectType: state => {
		return (type: string | number) => {
			if (typeof type == "string") {
				return state.metadataObjectTypes.find(ot => ot.tableName.toLowerCase() == type.toLowerCase());
			} else if (typeof type == "number") {
				return state.metadataObjectTypes.find(ot => ot.objectTypeId == type);
			}
		};
	},

	/**
	 * Gets Metadata for a given object lookup. The lookup must specify an object Id,
	 * but the metadata type/object types are optional.
	 *
	 * Metadata Type/Object Type can be the Id, object, or title that represents them.
	 */
	getMetadataForObject: (state, getters) => {
		return (lookup: MetadataLookup) => {
			let results: Metadata[] = [];

			const objectId = lookup.objectId;
			const objectTypeId = lookup.objectType ? getters.getObjectType(lookup.objectType)?.objectTypeId : null;
			const metadataTypeId = lookup.metadataType
				? getters.getMetadataType(lookup.metadataType)?.metadataTypeId
				: null;

			if (objectTypeId != null && metadataTypeId != null) {
				// We've got an objectId, objectTypeId, and metadataTypeId,
				// we can perform a direct lookup in the metadata Map.
				let lookupIndex = {
					objectId,
					objectTypeId,
					metadataTypeId
				};

				results = state.metadata[JSON.stringify(lookupIndex)];
			} else {
				// As we haven't got a type/metadata type, we're going to have
				// to search the Map for any possible matching metadata items.
				const metadataValues = Object.values(state.metadata).flatMap(m => m);
				results = metadataValues.filter(value => {
					const objectTypeMatches =
						objectTypeId == null ? true : value.objectType.objectTypeId == objectTypeId;
					const metadataTypeMatches =
						metadataTypeId == null ? true : value.metadataType.metadataTypeId == metadataTypeId;

					if (value.objectId == objectId && objectTypeMatches && metadataTypeMatches) {
						return true;
					}

					return false;
				});
			}

			// If we've only got one result, return that directly;
			// otherwise return the full array.
			if (!results || results.length == 0) {
				return null;
			} else {
				return results.length == 1 ? results[0] : results;
			}
		};
	}
};
